import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Video, Loader2, BookOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const INTERVIEW_TYPES = [
  { id: "technical", label: "Technical" },
  { id: "scenario", label: "Scenario" },
  { id: "architecture", label: "Architecture" },
  { id: "sql", label: "SQL" },
  { id: "behavioral", label: "Behavioral" },
];
const TRACKS = [
  { id: "data_cloud", label: "Data Cloud" },
  { id: "sfmc", label: "SFMC" },
  { id: "mc_next", label: "MC Next" },
];

export default function InviteInterviewModal({ candidate, onClose }) {
  const [interviewType, setInterviewType] = useState("technical");
  const [track, setTrack] = useState("data_cloud");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [knowledgeBaseId, setKnowledgeBaseId] = useState("");

  useEffect(() => {
    api.get("/recruiter/knowledge-bases").then((r) => setKnowledgeBases(r.data)).catch(() => setKnowledgeBases([]));
  }, []);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/recruiter/invite-interview/${candidate.user_id}`, { interview_type: interviewType, track, note: note.trim(), knowledge_base_id: knowledgeBaseId || undefined });
      toast.success(`Interview invite sent to ${candidate.name}`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="invite-interview-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Video className="w-4 h-4 text-purple-300" /> Invite {candidate.name} to Interview</h3>
          <button onClick={onClose} data-testid="invite-interview-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Interview Type</label>
        <select value={interviewType} onChange={(e) => setInterviewType(e.target.value)} data-testid="invite-interview-type-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400">
          {INTERVIEW_TYPES.map((t) => <option key={t.id} value={t.id} className="bg-[#0f1522]">{t.label}</option>)}
        </select>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-3 block">Track</label>
        <select value={track} onChange={(e) => setTrack(e.target.value)} data-testid="invite-interview-track-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400">
          {TRACKS.map((t) => <option key={t.id} value={t.id} className="bg-[#0f1522]">{t.label}</option>)}
        </select>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-3 block">Note (optional)</label>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder="Add a personal note about this interview invite..."
          data-testid="invite-interview-note-textarea"
          className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
        />

        {knowledgeBases.length > 0 && (
          <div className="mt-3">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> Use Your Knowledge Base (optional)</label>
            <select value={knowledgeBaseId} onChange={(e) => setKnowledgeBaseId(e.target.value)} data-testid="invite-interview-knowledge-base-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400">
              <option value="" className="bg-[#0f1522]">None — use generic questions</option>
              {knowledgeBases.map((kb) => <option key={kb.id} value={kb.id} className="bg-[#0f1522]">{kb.name}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">The candidate will be asked company-specific questions drawn from this knowledge base.</p>
          </div>
        )}

        <button
          onClick={send} disabled={sending} data-testid="invite-interview-send-btn"
          className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Send Interview Invite
        </button>
      </div>
    </div>
  );
}
